import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/lib/api";
import PortalFilings from "@/components/PortalFilings";
import { CalendarDays, Paperclip, User, CheckCircle2, ChevronDown } from "lucide-react";

const STATUS = {
  pending: ["Not started", "bg-slate-100 text-slate-600 border-slate-200"],
  attended: ["Seen", "bg-sky-50 text-sky-700 border-sky-200"],
  "in-progress": ["In progress", "bg-amber-50 text-amber-800 border-amber-200"],
  awaiting: ["Awaiting info", "bg-violet-50 text-violet-700 border-violet-200"],
  "on-hold": ["On hold", "bg-orange-50 text-orange-700 border-orange-200"],
  done: ["Completed", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

const StatusPill = ({ status, testid }) => {
  const [label, cls] = STATUS[status] || STATUS.pending;
  return <span data-testid={testid} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border whitespace-nowrap ${cls}`}>{label}</span>;
};

const Progress = ({ done, total }) => (
  <div className="flex items-center gap-2 text-xs text-slate-500">
    <div className="w-28 h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
    </div>
    {done}/{total} sub-tasks
  </div>
);

const TaskCard = ({ t, token }) => {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`portal-task-${t.id}`} className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-medium text-slate-900">{t.name}</h3>
        <StatusPill status={t.status} testid={`portal-task-status-${t.id}`} />
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />Due {fmt(t.due_date)}</span>
        {t.assignee_name && <span className="inline-flex items-center gap-1" data-testid={`portal-task-assignee-${t.id}`}><User className="w-3.5 h-3.5" />{t.assignee_name}</span>}
        <span>Updated {fmt(t.updated_at)}</span>
        {t.subtasks.total > 0 && <Progress done={t.subtasks.completed} total={t.subtasks.total} />}
      </div>
      {t.attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Task files</span>
          {t.attachments.map((a) => (
            <a key={a.id} data-testid={`portal-attachment-${a.id}`} href={`${API}/portal/${token}/attachments/${a.id}`}
               className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 hover:bg-slate-50 text-slate-700">
              <Paperclip className="w-3 h-3" />{a.filename}
            </a>
          ))}
        </div>
      )}
      {t.subtasks.total > 0 && (
        <button data-testid={`portal-task-toggle-${t.id}`} onClick={() => setOpen(!open)} className="mt-3 text-xs text-slate-600 inline-flex items-center gap-1 hover:text-slate-900">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />{open ? "Hide" : "Show"} sub-tasks
        </button>
      )}
      {open && (
        <ul className="mt-2 border-t border-slate-100 pt-2 space-y-1.5">
          {t.subtask_items.map((s) => (
            <li key={s.id} data-testid={`portal-subtask-${s.id}`} className="flex items-center justify-between text-sm" style={{ paddingLeft: (s.depth - 1) * 16 }}>
              <span className="text-slate-700 truncate">{s.name}</span>
              <StatusPill status={s.status} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fy, setFy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portal/${token}`, { params: fy ? { fy } : {} }).then((r) => setData(r.data)).catch((e) => setError(formatErr(e)));
  }, [token, fy]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div data-testid="portal-error" className="bg-white border border-slate-200 rounded-md p-8 max-w-md text-center text-slate-600">{error}</div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="portal-loading">Loading…</div>;

  const done = data.tasks.filter((t) => t.status === "done").length;
  return (
    <div className="App min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{data.org_name || "KMS-Obligo"} · Client portal</div>
        <h1 data-testid="portal-client-name" className="text-4xl sm:text-5xl font-semibold tracking-tight mt-1">{data.client_name}</h1>
        <div className="mt-3 text-sm text-slate-500 flex items-center gap-2" data-testid="portal-summary">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />{done} of {data.tasks.length} tasks completed
        </div>
        {data.tasks.length === 0
          ? <div data-testid="portal-empty" className="mt-10 text-sm text-slate-500">No tasks to show yet.</div>
          : <ul className="mt-8 space-y-3">{data.tasks.map((t) => <TaskCard key={t.id} t={t} token={token} />)}</ul>}
        {data.filings && <PortalFilings months={data.filings} fy={fy} fyOptions={data.fy_options} onFy={setFy} token={token} />}
        <div className="mt-10 text-xs text-slate-400">Read-only view · link valid until {fmt(data.expires_at)}</div>
      </div>
    </div>
  );
}
